import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { BaseComponent } from '@macs-frontend/core';
import { UUID } from '@macs-frontend/core/utils/uuid';
import { makeSelectable, SelectSubscribe } from '@macs-frontend/decorators';
import { defaultUserPermissions, getUserPermissions } from '@macs-frontend/reducers/meta';
import { Store } from '@ngrx/store';
import { function as FN, option as O } from 'fp-ts';
import { FormGroupState } from 'ngrx-forms';
import { WizardTabs } from '../../enums/wizard-tabs.enum';
import { Role, UserRoleUnit } from '../../models/role.model';
import { Tab, TabEl } from '../../models/tab.model';
import { User, UserForm, UserStatuses } from '../../models/user.model';

const tabs: Tab[] = [
  { name: WizardTabs.GENERAL, nextTabName: WizardTabs.PERMISSIONS },
  { name: WizardTabs.PERMISSIONS, nextTabName: null },
];

@Component({
  /* eslint-disable-next-line @angular-eslint/component-selector */
  selector: 'md-edit-user-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: 'edit-user-header.component.html',
})
export class UserDetailsHeaderComponent extends BaseComponent implements AfterViewInit {
  // @ts-expect-error
  @ViewChild('generalTab') generalTab: ElementRef;
  // @ts-expect-error
  @ViewChild('permissionsTab') permissionsTab: ElementRef;

  @Input() user?: User | null;
  // @ts-expect-error
  @Input() form: FormGroupState<UserForm>;
  // @ts-expect-error
  @Input() currentTab: Tab;
  // @ts-expect-error
  @Input() isFormValid: boolean;
  @Input() showValidationError = false;
  @Input() currentCompanyId?: UUID | null;
  @Input() sendingInvitation = false;

  @Output() _close: EventEmitter<void> = new EventEmitter<void>();
  @Output() _removeRole: EventEmitter<Role> = new EventEmitter<Role>();
  @Output() _save: EventEmitter<void> = new EventEmitter<void>();
  @Output() _exit: EventEmitter<void> = new EventEmitter<void>();
  @Output() _setActiveTab: EventEmitter<Tab> = new EventEmitter<Tab>();
  @Output() _sendInvite: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  _showValidationError: EventEmitter<boolean> = new EventEmitter<boolean>();

  @SelectSubscribe(getUserPermissions)
  userPermissions = defaultUserPermissions;

  tabsTypes = WizardTabs;
  userStatuses = UserStatuses;
  tabs: Tab[] = tabs;
  // @ts-expect-error
  tabsEls: TabEl[];
  translateX = 8;
  width = 56;

  ngAfterViewInit(): void {
    this.tabsEls = [
      { name: WizardTabs.GENERAL, el: this.generalTab },
      { name: WizardTabs.PERMISSIONS, el: this.permissionsTab },
    ];
  }

  close() {
    this._close.emit();
  }

  removeRole(role: Role) {
    this._removeRole.emit(role);
  }

  save() {
    this._save.emit();
  }

  exit() {
    if (!this.isFormValid) {
      return this._showValidationError.emit(true);
    }

    this._showValidationError.emit(false);
    this._exit.emit();
  }

  setActiveTab(tab: Tab, event: { target: any }) {
    if (!this.isFormValid) {
      return this._showValidationError.emit(true);
    }

    this._showValidationError.emit(false);

    this._setActiveTab.emit(tab);
    this.width = event.target.offsetWidth;
    this.translateX = event.target.offsetLeft;
  }

  nextTab() {
    if (!this.isFormValid) {
      return this._showValidationError.emit(true);
    }

    this._showValidationError.emit(false);

    // @ts-expect-error
    const nextTab: Tab = this.tabs.find(t => t.name === this.currentTab.nextTabName);

    // @ts-expect-error
    const tabEl: TabEl = this.tabsEls.find(t => t.name === nextTab.name);
    const event: Event = new MouseEvent('click', { bubbles: false });
    tabEl.el.nativeElement.dispatchEvent(event);
  }

  sendInvite() {
    this._sendInvite.emit();
  }

  getRoles(): UserRoleUnit[] {
    return FN.pipe(
      this.currentCompanyId,
      O.fromNullable,
      O.fold(
        () => this.user?.roleUnits || [],
        currentCompanyId =>
          this.user?.roleUnits?.filter(
            roleUnit => roleUnit.company && roleUnit.company.uuid === currentCompanyId,
          ) || [],
      ),
    );
  }

  showNewUserTitle(): boolean {
    return (
      !this.form.value.title &&
      this.form.controls.title.isPristine &&
      !this.form.value.lastName &&
      this.form.controls.lastName.isPristine &&
      !this.form.value.firstName &&
      this.form.controls.firstName.isPristine &&
      !this.user?.uuid
    );
  }

  shouldSendInvite() {
    return (
      !this.user?.uuid ||
      [UserStatuses.DRAFT, UserStatuses.CANCELED_INVITATION].indexOf(this.user?.status) >
        -1
    );
  }

  constructor(readonly store$: Store, readonly cdr: ChangeDetectorRef) {
    super();
    makeSelectable(this, { cdr, store$ });
  }
}
